<script lang="ts">
  import { coalesceText } from "$lib/animation/coalesce";
  import { onMount } from "svelte";

  /**
   * ChoiceGrid — 2x2 grid of options selectable via A/S/D/F hotkeys.
   *
   * Layout:
   *   [A] top-left     [S] top-right
   *   [D] bottom-left  [F] bottom-right
   *
   * When `animateReveal` is true (default), options animate in via a
   * coalesce effect — characters cycle through random glyphs before
   * resolving left-to-right. Buttons are non-interactive until the
   * animation completes.
   */

  interface ChoiceOption {
    label: string;
    text: string;
    category: string;
  }

  interface Props {
    options: ChoiceOption[];
    onSelect: (index: number) => void;
    disabled?: boolean;
    animateReveal?: boolean;
  }

  let { options, onSelect, disabled = false, animateReveal = true }: Props = $props();

  const HOTKEYS = ["a", "s", "d", "f"] as const;
  const HOTKEY_LABELS = ["A", "S", "D", "F"] as const;

  /** Currently highlighted option index (-1 = none). */
  let focusIndex = $state(-1);

  /** Whether the coalesce animation has finished revealing all options. */
  let revealed = $state(true);

  /** Display text for each option (scrambled during coalesce, real after). */
  let displayTexts: string[] = $state([]);

  /** Active cancel functions for in-flight coalesce animations. */
  let cancelFns: (() => void)[] = [];

  /**
   * Run the coalesce animation exactly once when the component mounts.
   * Uses onMount (not $effect) to guarantee single execution — $effect can
   * re-run multiple times during Svelte 5's reactive reconciliation, which
   * cancels in-flight timers before they resolve.
   * The parent should use {#key choices} to remount this component when
   * options change, triggering a fresh onMount.
   */
  onMount(() => {
    const currentOptions = options;

    if (!animateReveal || disabled || currentOptions.length === 0) {
      displayTexts = currentOptions.map((o) => o.text);
      revealed = true;
      return;
    }

    // Start unrevealed.
    revealed = false;
    displayTexts = currentOptions.map((o) => o.text);

    let doneCount = 0;
    const total = currentOptions.length;

    for (let i = 0; i < total; i++) {
      const idx = i;
      const cancel = coalesceText(
        currentOptions[idx].text,
        (text) => {
          displayTexts[idx] = text;
        },
        {
          onDone: () => {
            doneCount++;
            if (doneCount === total) {
              revealed = true;
            }
          },
        },
      );
      cancelFns.push(cancel);
    }

    // Cleanup on unmount.
    return () => {
      for (const cancel of cancelFns) {
        cancel();
      }
      cancelFns = [];
    };
  });

  /** Whether the grid is effectively non-interactive. */
  let effectivelyDisabled = $derived(disabled || !revealed);

  function selectOption(index: number): void {
    if (effectivelyDisabled || index < 0 || index >= options.length) return;
    onSelect(index);
  }

  function handleKeydown(e: KeyboardEvent): void {
    if (effectivelyDisabled) return;

    const key = e.key.toLowerCase();

    // Hotkey commit
    const hotkeyIdx = HOTKEYS.indexOf(key as (typeof HOTKEYS)[number]);
    if (hotkeyIdx !== -1 && hotkeyIdx < options.length) {
      e.preventDefault();
      focusIndex = hotkeyIdx;
      selectOption(hotkeyIdx);
      return;
    }

    // Arrow / WASD navigation (highlight only, no commit)
    switch (key) {
      case "arrowleft":
        e.preventDefault();
        if (focusIndex % 2 === 1) focusIndex -= 1;
        break;
      case "a":
        // already handled above for 'a' as hotkey
        break;
      case "arrowup":
      case "w":
        e.preventDefault();
        if (focusIndex >= 2) focusIndex -= 2;
        break;
      case "arrowdown":
        e.preventDefault();
        if (focusIndex < 2) focusIndex += 2;
        else if (focusIndex === -1) focusIndex = 0;
        break;
      case "arrowright":
        e.preventDefault();
        if (focusIndex % 2 === 0) focusIndex += 1;
        break;
    }
  }

  /** Produce an ASCII-bordered cell string. */
  function borderTop(): string {
    return "+================================+";
  }
  function borderBottom(): string {
    return "+================================+";
  }
</script>

<svelte:window onkeydown={handleKeydown} />

<div class="choice-grid" class:choice-grid--disabled={disabled}>
  {#each options as option, i}
    <button
      class="choice-cell"
      class:choice-cell--focused={focusIndex === i}
      class:choice-cell--unrevealed={!revealed && animateReveal}
      disabled={effectivelyDisabled}
      onclick={() => selectOption(i)}
    >
      <div class="choice-cell__border-top">{borderTop()}</div>
      <div class="choice-cell__body">
        <span class="choice-cell__hotkey">[{HOTKEY_LABELS[i]}]</span>
        <span class="choice-cell__category">{option.category}</span>
        <span class="choice-cell__text">{displayTexts[i] ?? option.text}</span>
      </div>
      <div class="choice-cell__border-bottom">{borderBottom()}</div>
    </button>
  {/each}
</div>

<style>
  .choice-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
    gap: 0.2rem;
    padding: 0.25rem 0.5rem;
    max-height: 30vh;
    overflow-y: auto;
    border-top: 1px solid var(--fg-dim);
  }

  .choice-grid--disabled {
    opacity: 0.35;
    pointer-events: none;
  }

  .choice-cell {
    background: transparent;
    border: none;
    color: var(--fg);
    font-family: var(--font-mono);
    font-size: 0.8rem;
    text-align: left;
    cursor: pointer;
    padding: 0;
    display: flex;
    flex-direction: column;
    min-height: 36px;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }

  .choice-cell:hover,
  .choice-cell--focused {
    color: var(--fg-bright);
  }

  .choice-cell--focused .choice-cell__border-top,
  .choice-cell--focused .choice-cell__border-bottom,
  .choice-cell:hover .choice-cell__border-top,
  .choice-cell:hover .choice-cell__border-bottom {
    color: var(--fg-bright);
  }

  .choice-cell--focused .choice-cell__body,
  .choice-cell:hover .choice-cell__body {
    background: rgba(51, 255, 51, 0.08);
  }

  .choice-cell__border-top,
  .choice-cell__border-bottom {
    color: var(--fg-dim);
    line-height: 1;
    overflow: hidden;
    white-space: nowrap;
    font-size: 0.7rem;
  }

  .choice-cell__body {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    padding: 0.15rem 0.4rem;
    border-left: 1px solid var(--fg-dim);
    border-right: 1px solid var(--fg-dim);
  }

  .choice-cell__hotkey {
    font-weight: bold;
    color: var(--fg-bright);
    font-size: 0.75rem;
  }

  .choice-cell__category {
    font-size: 0.65rem;
    color: var(--fg-dim);
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }

  .choice-cell__text {
    color: inherit;
    line-height: 1.25;
    font-size: 0.78rem;
  }

  .choice-cell--unrevealed {
    opacity: 0.6;
    cursor: default;
  }

  .choice-cell:active:not(:disabled) {
    background: rgba(51, 255, 51, 0.1);
  }

  @media (max-width: 600px) {
    .choice-grid {
      grid-template-columns: 1fr;
      grid-template-rows: auto;
      gap: 0.15rem;
      max-height: 35vh;
      padding: 0.25rem;
    }

    .choice-cell {
      min-height: 32px;
    }

    .choice-cell__body {
      flex-direction: row;
      align-items: center;
      gap: 0.4rem;
      padding: 0.2rem 0.4rem;
    }

    .choice-cell__hotkey {
      flex-shrink: 0;
    }

    .choice-cell__category {
      display: none;
    }

    .choice-cell__text {
      font-size: 0.75rem;
    }

    .choice-cell__border-top,
    .choice-cell__border-bottom {
      display: none;
    }
  }

  @media (max-width: 480px) {
    .choice-grid {
      max-height: 30vh;
    }

    .choice-cell__text {
      font-size: 0.7rem;
    }
  }
</style>
